import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ActionButton } from '@/components/common'
import { useUIStore, useAlertStore } from '@/store'
import { Bell, ChevronRight, X } from 'lucide-react'
import clsx from 'clsx'

interface DisplayAlert {
  id: string
  asset_id: string
  title: string
  message: string
  severity: 'critical'
  timestamp: string
  acknowledged: boolean
}

export default function CriticalAlertsBanner() {
  const navigate = useNavigate()
  const { addNotification } = useUIStore()
  const { alerts: storeAlerts } = useAlertStore()
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const [acknowledged, setAcknowledged] = useState<Set<string>>(new Set())
  const [expanded, setExpanded] = useState(false)

  // Convert store alerts to display format, filtering for critical/high only
  const alerts: DisplayAlert[] = useMemo(() => {
    const criticalAlerts = storeAlerts.filter(
      a => a.severity === 'critical' || a.severity === 'high'
    )
    return criticalAlerts.map(alert => ({
      id: alert.id,
      asset_id: alert.asset_id,
      title: alert.title,
      message: alert.message,
      severity: 'critical' as const,
      timestamp: new Date(alert.created_at).toLocaleString(),
      acknowledged: acknowledged.has(alert.id)
    }))
  }, [storeAlerts, acknowledged])

  const visibleAlerts = alerts.filter(a => !dismissed.has(a.id))

  if (visibleAlerts.length === 0) return null

  const handleDismiss = (id: string) => {
    setDismissed(prev => new Set([...prev, id]))
  }

  const handleDismissAll = () => {
    setDismissed(new Set(alerts.map(a => a.id)))
    addNotification({
      type: 'info',
      title: 'Alerts Dismissed',
      message: 'All critical alerts have been dismissed'
    })
  }

  const handleInvestigate = (alert: DisplayAlert) => {
    addNotification({
      type: 'info',
      title: 'Investigating Alert',
      message: `Opening details for ${alert.asset_id}...`
    })
    navigate(`/assets/${alert.asset_id}`)
  }

  const handleAcknowledge = (alert: DisplayAlert) => {
    setAcknowledged(prev => new Set([...prev, alert.id]))
    addNotification({
      type: 'success',
      title: 'Alert Acknowledged',
      message: `Alert for ${alert.asset_id} has been acknowledged`
    })
  }

  return (
    <div className="mb-4">
      {/* Summary Banner */}
      <div
        className={clsx(
          'bg-status-critical/10 border border-status-critical/30 rounded-lg',
          'px-4 py-3 flex items-center justify-between cursor-pointer',
          'hover:bg-status-critical/15 transition-colors'
        )}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="relative">
            <Bell className="text-status-critical" size={20} />
            <span className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center text-xs font-bold bg-status-critical text-white rounded-full">
              {visibleAlerts.length}
            </span>
          </div>
          <div>
            <span className="font-medium text-status-critical">
              {visibleAlerts.length} Critical Alert{visibleAlerts.length > 1 ? 's' : ''}
            </span>
            <span className="text-surface-400 text-sm ml-2">
              Immediate attention required
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ActionButton
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              handleDismissAll()
            }}
          >
            Dismiss All
          </ActionButton>
          <ChevronRight
            size={20}
            className={clsx(
              'text-surface-400 transition-transform',
              expanded && 'rotate-90'
            )}
          />
        </div>
      </div>

      {/* Expanded Alert List */}
      {expanded && (
        <div className="mt-2 space-y-2 animate-in">
          {visibleAlerts.map((alert) => (
            <div
              key={alert.id}
              className={clsx(
                'bg-surface-800 border rounded-lg p-4 flex items-start justify-between',
                alert.acknowledged ? 'border-surface-600' : 'border-surface-700'
              )}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-sm text-primary-400">
                    {alert.asset_id}
                  </span>
                  <span className="text-xs text-surface-500">
                    {alert.timestamp}
                  </span>
                  {alert.acknowledged && (
                    <span className="text-xs bg-status-healthy/20 text-status-healthy px-2 py-0.5 rounded">
                      Acknowledged
                    </span>
                  )}
                </div>
                <h4 className="font-medium text-surface-200">{alert.title}</h4>
                <p className="text-sm text-surface-400 mt-1">{alert.message}</p>
              </div>
              <div className="flex items-center gap-2 ml-4">
                <ActionButton
                  variant="primary"
                  size="sm"
                  onClick={() => handleInvestigate(alert)}
                >
                  Investigate
                </ActionButton>
                <ActionButton
                  variant="secondary"
                  size="sm"
                  onClick={() => handleAcknowledge(alert)}
                  disabled={alert.acknowledged}
                >
                  {alert.acknowledged ? 'Acknowledged' : 'Acknowledge'}
                </ActionButton>
                <button
                  onClick={() => handleDismiss(alert.id)}
                  className="p-1 hover:bg-surface-700 rounded"
                >
                  <X size={16} className="text-surface-400" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
